/** The node types beyond the first eighteen, in the order the gallery shows them. */
export const CONTROL_TYPE_LIST = [
    // data display
    "data-grid", "list-view", "item-card", "item-card-group", "kpi", "kpi-group", "widget", "timeline", "agenda", "kanban", "file-tree", "empty-state", "carousel", "hover-card", "holo-card", "trend-chip", "number-value", "rating", "meter", "progress-circle", "spinner", "skeleton", "alert", "badge", "avatar", "avatar-group", "kbd", "tooltip", "typography", "markdown", "code-block", "chain-of-thought", "text-shimmer", "color-swatch",
    // charts
    "area-chart", "bar-chart", "line-chart", "composed-chart", "pie-chart", "radar-chart", "radial-chart", "scatter-chart", "bubble-chart", "histogram", "box-plot", "funnel-chart", "treemap", "heatmap", "sankey", "gauge", "sparkline", "waterfall-chart",
    // forms
    "text-area", "search-field", "input-group", "number-field", "number-stepper", "select", "autocomplete", "combo-box", "inline-select", "native-select", "cell-select", "checkbox", "checkbox-group", "checkbox-button-group", "radio-group", "radio-button-group", "switch", "cell-switch", "slider", "cell-slider", "segment", "date-field", "date-picker", "date-range-picker", "time-field", "calendar", "range-calendar", "tag-group", "color-field", "color-picker", "color-swatch-picker", "cell-color-picker", "rich-text-editor", "drop-zone",
    // navigation
    "tabs", "accordion", "disclosure", "disclosure-group", "breadcrumbs", "pagination", "stepper", "toolbar", "button-group", "toggle-button", "toggle-button-group", "dropdown", "action-bar",
    // overlays
    "sheet", "modal",
    // map
    "map",
];
/** The controls that hold a value, by kind. `named`: only a node that has a `name` is a field (a rating without one is a picture). */
export const CONTROL_FIELDS = {
    "list-view": { kind: "options", named: true },
    rating: { kind: "number", named: true },
    "text-area": { kind: "text" },
    "search-field": { kind: "text" },
    "input-group": { kind: "text" },
    "number-field": { kind: "number" },
    "number-stepper": { kind: "number" },
    select: { kind: "option" },
    autocomplete: { kind: "option" },
    "combo-box": { kind: "option" },
    "inline-select": { kind: "option" },
    "native-select": { kind: "option" },
    "cell-select": { kind: "option" },
    checkbox: { kind: "boolean" },
    "checkbox-group": { kind: "options" },
    "checkbox-button-group": { kind: "options" },
    "radio-group": { kind: "option" },
    "radio-button-group": { kind: "option" },
    switch: { kind: "boolean" },
    "cell-switch": { kind: "boolean" },
    slider: { kind: "number" },
    "cell-slider": { kind: "number" },
    segment: { kind: "option" },
    "date-field": { kind: "date" },
    "date-picker": { kind: "date" },
    "date-range-picker": { kind: "date-range" },
    "time-field": { kind: "time" },
    calendar: { kind: "date" },
    "range-calendar": { kind: "date-range" },
    "tag-group": { kind: "options" },
    "color-field": { kind: "color" },
    "color-picker": { kind: "color" },
    "color-swatch-picker": { kind: "color" },
    "cell-color-picker": { kind: "color" },
    "rich-text-editor": { kind: "text" },
    "drop-zone": { kind: "files" },
    tabs: { kind: "option", named: true },
    pagination: { kind: "number" },
    "toggle-button": { kind: "boolean" },
    "toggle-button-group": { kind: "option" },
};
/** What a node's value is, or undefined when the node holds none. A toggle-button-group with `multiple` holds several. */
export function fieldKindOf(node) {
    const n = node;
    const def = CONTROL_FIELDS[n.type];
    if (!def)
        return undefined;
    if (def.named && typeof n.name !== "string")
        return undefined;
    if (n.type === "toggle-button-group" && n.multiple === true)
        return "options";
    return def.kind;
}
/** The nodes directly below a node: its children, and the children of each of its panels. */
export function childNodes(node) {
    const n = node;
    const out = [...(n.children ?? [])];
    for (const p of n.panels ?? [])
        out.push(...p.children);
    return out;
}
/** Every `UiAction`-shaped object inside a node's own properties (not the nodes below it): `{ do: "run", name }` and the rest. */
export function ownActions(node) {
    const out = [];
    const seen = (v, depth) => {
        if (!v || typeof v !== "object" || depth > 5)
            return;
        if (Array.isArray(v)) {
            for (const x of v)
                seen(x, depth + 1);
            return;
        }
        const o = v;
        if (typeof o.do === "string" && ["send", "open-view", "run", "reset"].includes(o.do)) {
            out.push(o);
            return;
        }
        for (const [k, x] of Object.entries(o))
            if (k !== "children" && k !== "rows")
                seen(x, depth + 1);
    };
    const { children: _c, ...rest } = node;
    seen(rest, 0);
    return out;
}
const dig = (data, path) => {
    let cur = data;
    for (const part of path.split(".")) {
        if (cur === null || cur === undefined)
            return undefined;
        if (Array.isArray(cur))
            cur = /^\d+$/.test(part) ? cur[Number(part)] : undefined;
        else if (typeof cur === "object")
            cur = Object.prototype.hasOwnProperty.call(cur, part) ? cur[part] : undefined;
        else
            return undefined;
    }
    return cur;
};
/** The most keys one bound row keeps, and the longest a string cell is. */
const ROW_KEYS = 32;
const CELL_CHARS = 240;
/** One bound item as a flat record of cells: primitives kept, a list of primitives joined with commas (a sparkline's points), the rest dropped. */
export function flattenRecord(item) {
    const row = {};
    if (item === null || typeof item !== "object" || Array.isArray(item)) {
        if (typeof item === "string" || typeof item === "number" || typeof item === "boolean")
            row.value = typeof item === "string" ? item.slice(0, CELL_CHARS) : item;
        return row;
    }
    let n = 0;
    for (const [k, v] of Object.entries(item)) {
        if (n >= ROW_KEYS)
            break;
        if (!/^[A-Za-z][A-Za-z0-9_]{0,31}$/.test(k))
            continue;
        if (typeof v === "string")
            row[k] = v.slice(0, CELL_CHARS);
        else if (typeof v === "number" || typeof v === "boolean" || v === null)
            row[k] = v;
        else if (Array.isArray(v) && v.length <= 200 && v.every((x) => typeof x === "number" || typeof x === "string"))
            row[k] = v.join(",").slice(0, CELL_CHARS * 4);
        else
            continue;
        n++;
    }
    return row;
}
/** Rename the keys of rows: `keys = { title: "subject" }` gives each row a `title` read from its `subject`. */
export function remapRecords(rows, keys) {
    if (!keys || !Object.keys(keys).length)
        return rows;
    return rows.map((r) => {
        const out = { ...r };
        for (const [to, from] of Object.entries(keys))
            if (from in r)
                out[to] = r[from];
        return out;
    });
}
/**
 * A control's rows: its literal ones, or the binding's list named by `<prop>From`, flattened, capped and remapped by `keys`.
 * `prop` is `rows` for most controls; a kanban's columns and a chain's steps use their own.
 */
export function resolveRecords(node, data, prop = "rows", max = 2_000) {
    const n = node;
    const keys = n.keys;
    const literal = n[prop];
    if (Array.isArray(literal))
        return remapRecords(literal.slice(0, max).map(flattenRecord), keys);
    const from = n[`${prop}From`];
    if (typeof from !== "string")
        return [];
    const list = dig(data ?? {}, from);
    if (!Array.isArray(list))
        return [];
    return remapRecords(list.slice(0, max).map(flattenRecord), keys);
}
/** A choice control's options: literal, or a bound list read as `{ value, label, description? }` (the label is the value when missing). */
export function resolveOptions(node, data, max = 200) {
    const n = node;
    if (n.options)
        return n.options.slice(0, max);
    if (!n.optionsFrom)
        return [];
    const rows = resolveRecords({ ...node, rows: undefined, rowsFrom: n.optionsFrom }, data, "rows", max);
    const out = [];
    for (const r of rows) {
        const value = r.value === undefined || r.value === null ? "" : String(r.value);
        if (!value)
            continue;
        const label = r.label === undefined || r.label === null || r.label === "" ? value : String(r.label);
        out.push({ value: value.slice(0, 60), label: label.slice(0, 120), ...(typeof r.description === "string" && r.description ? { description: r.description.slice(0, 240) } : {}) });
    }
    return out;
}
// ── A field's value ──────────────────────────────────────────────────
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const ISO_RANGE = /^\d{4}-\d{2}-\d{2}\/\d{4}-\d{2}-\d{2}$/;
const HEX = /^#[0-9a-fA-F]{6}$/;
const TIME = /^\d{2}:\d{2}$/;
/** A field's raw value, cleaned by its kind and its node's bounds. Undefined: drop it. Files are not values (they travel as attachments). */
export function cleanFieldValue(node, raw, data) {
    const kind = fieldKindOf(node);
    const n = node;
    switch (kind) {
        case "text":
            return typeof raw === "string" ? raw.slice(0, typeof n.maxLength === "number" ? n.maxLength : 4_000) : undefined;
        case "boolean":
            return raw === true;
        case "number": {
            const x = typeof raw === "number" ? raw : typeof raw === "string" && raw.trim() !== "" ? Number(raw) : NaN;
            if (!Number.isFinite(x))
                return undefined;
            return Math.min(typeof n.max === "number" ? n.max : Infinity, Math.max(typeof n.min === "number" ? n.min : -Infinity, x));
        }
        case "option": {
            if (typeof raw !== "string")
                return undefined;
            if (n.optionsFrom)
                return raw.slice(0, 60);
            const options = resolveOptions(node, data);
            if (n.type === "tabs")
                return (n.panels ?? []).some((p) => p.id === raw) ? raw : undefined;
            return options.some((o) => o.value === raw) ? raw : undefined;
        }
        case "options": {
            if (!Array.isArray(raw))
                return undefined;
            const options = n.optionsFrom ? [] : resolveOptions(node, data);
            const known = new Set(options.map((o) => o.value));
            const list = raw.filter((v) => typeof v === "string" && v.length <= 60 && (known.size === 0 || known.has(v)));
            return [...new Set(list)].slice(0, 60);
        }
        case "date":
            return typeof raw === "string" && ISO_DATE.test(raw) ? raw : undefined;
        case "date-range":
            return typeof raw === "string" && ISO_RANGE.test(raw) ? raw : undefined;
        case "time":
            return typeof raw === "string" && TIME.test(raw) ? raw : undefined;
        case "color":
            return typeof raw === "string" && HEX.test(raw) ? raw.toLowerCase() : undefined;
        default:
            return undefined;
    }
}
/** What a field starts as: its `default`, cleaned; a boolean starts false. Files and unset fields start empty. */
export function controlDefault(node) {
    const kind = fieldKindOf(node);
    const n = node;
    if (!kind || kind === "files")
        return undefined;
    if (kind === "boolean")
        return n.default === true;
    if (kind === "options")
        return Array.isArray(n.default) ? n.default : typeof n.default === "string" ? [n.default] : [];
    if (n.default === undefined)
        return undefined;
    if (kind === "number")
        return typeof n.default === "number" ? n.default : undefined;
    return typeof n.default === "string" ? n.default : undefined;
}
//# sourceMappingURL=uiControlRules.js.map